import { useAuth } from "@clerk/clerk-react";
import {
  Avatar,
  Button,
  ButtonBase,
  Grid,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { BrandsContext } from "contexts/Brands";
import { DiscoverViewContext } from "contexts/DiscoverView";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UpgradeDialogSource } from "schemas/dashboard";
import { routes } from "schemas/routes";

import InboxSearchBarMenu from "features/Influencer/ContactList/NewOutreachTable/SearchBar/menu";
import { useOutreachTable } from "features/Influencer/ContactList/useOutreachTable";
import DiscoverSearchBar from "features/Influencer/Discover/Search";
import { isDemoUser } from "utils/demo";
import { isPage } from "utils/general";
import {
  DISPLAYED_INSTAGRAM_ONBOARDING_PAYWALL,
  DISPLAY_SCHEDULED_DIALOG,
  INSTAGRAM_ONBOARDING_CHOICE,
  ON_BEHALF_OF_TOKEN,
  SCHEDULED_DATE_EMAIL,
} from "utils/localStorage";
import { generateInitials } from "utils/string";
import { trackEvent } from "utils/tracking";
import { usePageSize } from "utils/usePageSize";

import ReferralDialogNavigation from "../ReferralDialog/navigation";
import ChatHistory from "./ChatHistory";
import EmailCount from "./EmailCount";
import TopNavigationLeftDrawer from "./LeftDrawer";
import TopNavigationMobile from "./mobile";
import styles from "./styles";

function TopNavigation() {
  const { isSmallScreen: isOutreachTableSmallScreen } = useOutreachTable();
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("lg"));
  const onBehalfToken = sessionStorage.getItem(ON_BEHALF_OF_TOKEN);

  const { signOut } = useAuth();
  const navigate = useNavigate();

  const { clearOrganizationContext, currentUser, currentOrg } = useContext(
    OrganizationUserContext,
  );

  const { discoverTab } = useContext(DiscoverViewContext);
  const { navigationWidth } = usePageSize();
  const { setUpgradeDialogSource } = useContext(SubscriptionContext);

  const [navigationReferDialog, setNavigationReferDialog] = useState(false);

  const showMobileNavigation =
    isMobileScreen || (isMediumScreen && discoverTab !== null);

  const { clearBrandContext } = useContext(BrandsContext);

  const [profileAnchor, setProfileAnchor] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    isProfileAnchor: boolean,
  ) => {
    setProfileAnchor(isProfileAnchor);
    trackEvent("Settings Menu Dropdown Clicked");
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogOut = () => {
    trackEvent("Logout Button Clicked");
    if (onBehalfToken) {
      sessionStorage.removeItem(ON_BEHALF_OF_TOKEN);
      window.open("/admin", "_self");
    } else if (currentOrg?.isBrand) {
      signOut(() => navigate(`/brands/sign-in`, { replace: true }));
    } else {
      signOut(() => navigate(`/sign-in`, { replace: true }));
    }

    // Reset all contexts.
    clearBrandContext();
    clearOrganizationContext();

    // Reset localStorage
    localStorage.removeItem(`${DISPLAY_SCHEDULED_DIALOG}-${currentOrg?.id}`);
    localStorage.removeItem(`${SCHEDULED_DATE_EMAIL}-${currentOrg?.id}`);
    localStorage.removeItem(
      `${DISPLAYED_INSTAGRAM_ONBOARDING_PAYWALL}-${currentOrg?.id}`,
    );
    localStorage.removeItem(INSTAGRAM_ONBOARDING_CHOICE);
  };

  const handleOpenReferral = () => {
    trackEvent("Refer A Friend Clicked", {
      "Clicked From": "Navigation Bar",
    });
    setNavigationReferDialog(true);
  };

  const profileMenu = (
    <Grid
      item
      container
      xs="auto"
      justifyContent="flex-end"
      alignItems="center"
    >
      <ButtonBase onClick={(e) => handleClick(e, true)}>
        <Avatar sx={styles.avatar}>
          {generateInitials(currentUser?.name || currentUser?.email || "")}
        </Avatar>
      </ButtonBase>
    </Grid>
  );

  return (
    <>
      {showMobileNavigation ? (
        <TopNavigationMobile
          width={navigationWidth}
          handleClick={handleClick}
          profileMenu={profileMenu}
        />
      ) : (
        <Grid
          sx={[
            styles.navigation,
            {
              width: navigationWidth,
            },
          ]}
          gap={1}
          justifyContent="space-between"
        >
          <Grid item xs="auto">
            <Grid container alignItems="center" gap={1}>
              {!currentOrg?.isBrand && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    setUpgradeDialogSource(UpgradeDialogSource.navigation_bar)
                  }
                  sx={{
                    borderRadius: 4,
                    textTransform: "none",
                    color: "green.main",
                  }}
                >
                  See Plans
                </Button>
              )}

              {!currentOrg?.isBrand && (
                <Button
                  size="small"
                  sx={{
                    borderRadius: 4,
                    textTransform: "none",
                    color: "green.main",
                  }}
                  onClick={handleOpenReferral}
                >
                  Refer a Friend
                </Button>
              )}
            </Grid>
          </Grid>

          <Grid item xs>
            {isPage(routes.brands) && <DiscoverSearchBar />}
            {isPage(routes.inbox) && !isOutreachTableSmallScreen && (
              <InboxSearchBarMenu />
            )}
          </Grid>

          {isPage(routes.home) && isDemoUser(currentOrg) && (
            <Grid item xs="auto" sx={{ mr: 5 }}>
              <ChatHistory />
            </Grid>
          )}
          <Grid
            container
            alignItems="center"
            justifyContent="flex-end"
            gap={2}
            item
            xs="auto"
          >
            <EmailCount />
          </Grid>
          {profileMenu}
        </Grid>
      )}

      <TopNavigationLeftDrawer
        handleLogOut={handleLogOut}
        open={open}
        handleClose={handleClose}
        openSettingsMenuOnly={!showMobileNavigation && profileAnchor}
        anchorEl={anchorEl}
      />

      <ReferralDialogNavigation
        open={navigationReferDialog}
        close={() => setNavigationReferDialog(false)}
        source="Top Navigation Bar"
      />
    </>
  );
}

export default TopNavigation;
